
import { defineComponent, PropType } from '@vue/composition-api';
import IllinoisIcons from './icons.vue';

interface TabItem {
  label: string;
  value: string;
  sportName?: string;
  sportId?: string;
  categoryId?: string;
  disabled?: boolean;
}

export default defineComponent({
  name: 'IllinoisTabBar',
  components: {
    IllinoisIcons
  },
  props: {
    items: {
      type: Array as PropType<TabItem[]>,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    activeTab: {
      type: String,
      default: null
    },
    hasBorderBottom: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'xs'
    },
    underline: {
      type: Boolean,
      default: true
    },
    borderTickens: {
      type: Number,
      default: 2
    },
    activeTabTickens: {
      type: Number,
      default: 2
    },
    darkText: {
      type: Boolean,
      default: false
    },
    centerTabs: {
      type: Boolean,
      default: false
    },
    showIconSkeleton: {
      type: Boolean,
      default: false
    },
    hasEdgeOffsetOnMobile: {
      type: Boolean,
      default: false
    },
    paddingLeft: {
      type: [String, Number],
      default: null
    },
    paddingRight: {
      type: [String, Number],
      default: null
    },
    itemGap: {
      type: [String, Number],
      default: null
    },
    uppercase: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      indicatorLeftPx: 0 as number,
      indicatorWidthPx: 0 as number,
      indicatorHeightPx: 0 as number,
      underlineOffsetPx: 0 as number,
      pillPaddingX: 10 as number,
      pillPaddingY: 2 as number,
      skeletonItems: [48, 64, 72, 54, 60] as number[],
      canScrollLeft: false as boolean,
      canScrollRight: false as boolean,
      scrollRafId: null as number | null
    };
  },
  methods: {
    selectTab(value: string) {
      this.$emit('update:activeTab', value);
    },
    formatLabel(label: string) {
      // Se la stringa è tutta maiuscola, la trasforma in capitalize (prima lettera maiuscola, resto minuscolo)
      if (label === label.toUpperCase()) {
        return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
      }
      if (label === label.toLowerCase()) {
        return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
      }
      // Altrimenti mantiene la formattazione originale
      return label;
    },
    updateIndicator() {
      const container = this.$refs.tabsContainer as any as HTMLElement | any;
      const itemRefs = this.$refs.itemRefs as any as HTMLElement[] | any;
      const labelRefs = this.$refs.labelRefs as any as HTMLElement[] | any;

      if (!container || !itemRefs || !Array.isArray(itemRefs)) {
        this.indicatorLeftPx = 0;
        this.indicatorWidthPx = 0;
        this.indicatorHeightPx = 0;
        return;
      }

      const activeIndex = Array.isArray(this.items)
        ? this.items.findIndex((it: any) => it && it.value === this.activeTab)
        : -1;
      if (activeIndex < 0) {
        this.indicatorLeftPx = 0;
        this.indicatorWidthPx = 0;
        this.indicatorHeightPx = 0;
        return;
      }

      const containerEl: HTMLElement =
        (container.$el as HTMLElement) || (container as HTMLElement);
      const targetItem: HTMLElement =
        (itemRefs[activeIndex]?.$el as HTMLElement) || itemRefs[activeIndex];
      const targetLabel: HTMLElement | undefined = labelRefs
        ? (labelRefs[activeIndex]?.$el as HTMLElement) || labelRefs[activeIndex]
        : undefined;

      const cRect = containerEl.getBoundingClientRect();
      const tRect = targetItem.getBoundingClientRect();
      const lRect = targetLabel?.getBoundingClientRect();

      // Calcola la posizione rispetto al contenuto scrollabile (non alla viewport)
      const scrollLeft = containerEl.scrollLeft;
      const rawLeftViewport = (lRect?.left ?? tRect.left) - cRect.left;
      const rawLeft = rawLeftViewport + scrollLeft;
      const rawWidth = (lRect?.width ?? tRect.width) as number;
      this.indicatorLeftPx = Math.round(rawLeft);
      this.indicatorWidthPx = Math.round(rawWidth);
      this.underlineOffsetPx = this.underline ? -1 : 0;
      // For pill mode (underline === false), match target item height
      this.indicatorHeightPx = this.underline
        ? this.activeTabTickens
        : tRect.height;
    },
    updateScrollState() {
      const container = this.$refs.tabsContainer as any as HTMLElement | any;
      if (!container) {
        this.canScrollLeft = false;
        this.canScrollRight = false;
        return;
      }

      const containerEl: HTMLElement =
        (container.$el as HTMLElement) || (container as HTMLElement);
      const scrollLeft = containerEl.scrollLeft;
      const scrollWidth = containerEl.scrollWidth;
      const clientWidth = containerEl.clientWidth;

      this.canScrollLeft = scrollLeft > 0;
      this.canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
    },
    scrollLeft() {
      const container = this.$refs.tabsContainer as any as HTMLElement | any;
      if (!container) return;

      const containerEl: HTMLElement =
        (container.$el as HTMLElement) || (container as HTMLElement);
      const scrollLeft = containerEl.scrollLeft;
      const clientWidth = containerEl.clientWidth;
      const scrollWidth = containerEl.scrollWidth;

      // Calcola la quantità di scroll come multiplo della larghezza visibile (80%)
      const scrollAmount = Math.floor(clientWidth * 0.8);

      // Calcola la nuova posizione di scroll
      let newScrollLeft = scrollLeft - scrollAmount;

      // Se rimangono meno del 20% della larghezza visibile, scrolla fino all'inizio
      if (newScrollLeft < clientWidth * 0.2) {
        newScrollLeft = 0;
      } else {
        // Arrotonda alla posizione più vicina che è multiplo della larghezza visibile
        newScrollLeft = Math.floor(newScrollLeft / clientWidth) * clientWidth;
      }

      containerEl.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    },
    scrollRight() {
      const container = this.$refs.tabsContainer as any as HTMLElement | any;
      if (!container) return;

      const containerEl: HTMLElement =
        (container.$el as HTMLElement) || (container as HTMLElement);
      const scrollLeft = containerEl.scrollLeft;
      const clientWidth = containerEl.clientWidth;
      const scrollWidth = containerEl.scrollWidth;
      const maxScrollLeft = scrollWidth - clientWidth;

      // Calcola la quantità di scroll come multiplo della larghezza visibile (80%)
      const scrollAmount = Math.floor(clientWidth * 0.8);

      // Calcola la nuova posizione di scroll
      let newScrollLeft = scrollLeft + scrollAmount;

      // Se rimangono meno del 20% della larghezza visibile, scrolla fino alla fine
      if (newScrollLeft > maxScrollLeft - clientWidth * 0.2) {
        newScrollLeft = maxScrollLeft;
      } else {
        // Arrotonda alla posizione più vicina che è multiplo della larghezza visibile
        newScrollLeft = Math.ceil(newScrollLeft / clientWidth) * clientWidth;
        // Assicurati di non superare il massimo
        newScrollLeft = Math.min(newScrollLeft, maxScrollLeft);
      }

      containerEl.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    },
    onScroll() {
      if (this.scrollRafId) return;
      this.scrollRafId = requestAnimationFrame(() => {
        this.scrollRafId = null;
        this.updateScrollState();
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateIndicator();
      this.updateScrollState();
      window.addEventListener(
        'resize',
        this.updateIndicator as any,
        {
          passive: true
        } as any
      );
      window.addEventListener(
        'resize',
        this.updateScrollState as any,
        {
          passive: true
        } as any
      );
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateIndicator as any);
    window.removeEventListener('resize', this.updateScrollState as any);
    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId);
    }
  },
  watch: {
    loading: {
      handler(newVal) {
        if (!newVal) {
          this.$nextTick(() => {
            this.updateIndicator();
            this.updateScrollState();
          });
        }
      },
      immediate: false
    },
    items: {
      handler(newItems) {
        const hasValidActive = Array.isArray(newItems)
          ? newItems.some((it: any) => it && it.value === this.activeTab)
          : false;
        if (!hasValidActive) {
          const nextValue =
            Array.isArray(newItems) && newItems.length > 0
              ? newItems[0].value
              : null;
          if (nextValue !== this.activeTab) {
            this.$emit('update:activeTab', nextValue);
          }
        }
        this.$nextTick(() => {
          this.updateIndicator();
          this.updateScrollState();
        });
      },
      immediate: false
    },
    activeTab() {
      this.$nextTick(() => {
        this.updateIndicator();
        this.updateScrollState();
      });
    }
  }
});
